import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Collection, CollectionReport, CollectionVariant } from "../../../api/derin";
import VariantRow from "./VariantRow";
import {
  accentButtonSx,
  appliedChipSx,
  captionLabelSx,
  chipSx,
  collectionColors,
  font,
  formatDate,
  formatFileSize,
  outlinedButtonSx,
  subtitleSx,
} from "./tokens";

/** One labelled row of file pills. Wraps, so a long report list pushes down not out. */
const fileRowSx = {
  display: "flex",
  flexDirection: "row" as const,
  alignItems: "center",
  gap: "8px",
  flexWrap: "wrap" as const,
};

const iconButtonSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "28px",
  height: "28px",
  padding: 0,
  background: "transparent",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  "&:hover": { background: "rgba(0, 0, 0, 0.04)" },
};

/** A file the collection does not have: stated, not dressed up as a pill. */
const emptyFileSx = {
  ...subtitleSx,
  fontStyle: "italic" as const,
};

const ellipsisSx = {
  // minWidth:0 is what makes the ellipsis appear at all: a flex item defaults to
  // min-width:auto and refuses to shrink below its text.
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap" as const,
};

interface CollectionCardProps {
  collection: Collection;
  expanded: boolean;
  onToggleExpand: () => void;
  /** The accordion's contents are still being fetched — the listing does not carry them. */
  isLoadingDetail?: boolean;
  /** Why the accordion's contents could not be fetched, if they could not. */
  detailError?: string | null;
  onRetryDetail?: () => void;
  /** Live status, so an optimistic toggle shows before the list is refetched. */
  isActive: boolean;
  isTogglingStatus: boolean;
  onToggleStatus: () => void;
  /** surveyOriginalId whose version history is open, if any. */
  openHistoryFor: string | null;
  onToggleVariantHistory: (surveyOriginalId: string) => void;
  onAddVariant: () => void;
  onUploadConfigFile: () => void;
  onUploadAboutFile: () => void;
  onRemoveAboutFile: () => void;
  onUploadReports: () => void;
  onRemoveReport: (report: CollectionReport) => void;
  onRenameVariant: (variant: CollectionVariant, label: string) => void;
  onUploadVersion: (variant: CollectionVariant) => void;
  onSetPrimaryVariant: (variant: CollectionVariant) => void;
  onRemoveVariant: (variant: CollectionVariant) => void;
  onDownloadConfig: () => void;
  /** Opens the delete-collection confirmation from the card's overflow menu. */
  onDelete: () => void;
  /** Live value, so an optimistic toggle shows before the listing is refetched. */
  earlyAccessEnabled: boolean;
  isTogglingEarlyAccess: boolean;
  onToggleEarlyAccess: () => void;
}

const CollectionCard: React.FC<CollectionCardProps> = ({
  collection,
  expanded,
  onToggleExpand,
  isLoadingDetail = false,
  detailError = null,
  onRetryDetail,
  isActive,
  isTogglingStatus,
  onToggleStatus,
  openHistoryFor,
  onToggleVariantHistory,
  onAddVariant,
  onUploadConfigFile,
  onUploadAboutFile,
  onRemoveAboutFile,
  onUploadReports,
  onRemoveReport,
  onRenameVariant,
  onUploadVersion,
  onSetPrimaryVariant,
  onRemoveVariant,
  onDownloadConfig,
  onDelete,
  earlyAccessEnabled,
  isTogglingEarlyAccess,
  onToggleEarlyAccess,
}) => {
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchor);

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        border: `1px solid ${collectionColors.border}`,
        borderRadius: "10px",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        onClick={onToggleExpand}
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "16px",
          padding: "17px",
          cursor: "pointer",
          "&:hover": { background: "rgba(0, 0, 0, 0.01)" },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <LayersOutlinedIcon sx={{ width: 16, height: 16, color: collectionColors.textSecondary }} />
            <Typography
              sx={{
                fontFamily: font,
                fontWeight: 500,
                fontSize: "16px",
                lineHeight: "24px",
                color: collectionColors.textPrimary,
              }}
            >
              {collection.name}
            </Typography>
            <Tooltip title={isActive ? "Click to deactivate" : "Click to activate"}>
              <Box
                component="button"
                disabled={isTogglingStatus}
                // The whole header is the expand target, so the toggle has to keep its
                // click to itself.
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation();
                  onToggleStatus();
                }}
                sx={{
                  ...chipSx,
                  gap: "6px",
                  background: "transparent",
                  cursor: "pointer",
                  "&:hover": { background: "rgba(0, 0, 0, 0.04)" },
                  "&:disabled": { opacity: 0.6, cursor: "default" },
                }}
              >
                {isTogglingStatus && (
                  <CircularProgress
                    size={12}
                    sx={{ color: collectionColors.textPrimary }}
                  />
                )}
                {isActive ? "Active" : "Inactive"}
              </Box>
            </Tooltip>
          </Box>

          {collection.description && (
            <Typography sx={{ ...subtitleSx, fontSize: "14px", lineHeight: "20px" }}>
              {collection.description}
            </Typography>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <Typography sx={{ ...subtitleSx, fontSize: "14px", lineHeight: "20px" }}>
              Added {formatDate(collection.createdAt)}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <FolderOutlinedIcon sx={{ width: 14, height: 14, color: collectionColors.textSecondary }} />
              <Typography sx={{ ...subtitleSx, fontSize: "14px", lineHeight: "20px" }}>
                {/* variantCount, not variants.length: the listing leaves the variant
                    rows empty until the card is opened. */}
                {collection.variantCount} variant{collection.variantCount === 1 ? "" : "s"}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "2px", flexShrink: 0 }}>
          {/* The header is the expand target, so the overflow menu keeps its clicks. */}
          <IconButton
            size="small"
            aria-label="Collection actions"
            onClick={(event) => {
              event.stopPropagation();
              setMenuAnchor(event.currentTarget);
            }}
          >
            <MoreVertIcon sx={{ width: 20, height: 20, color: collectionColors.textSecondary }} />
          </IconButton>
          {/* The Menu renders in a portal, but React synthetic events still bubble up the
              React tree to the header's onToggleExpand. Without this stopPropagation a
              backdrop click (or any click on the menu paper outside the item) would toggle
              the card. */}
          <Box onClick={(event) => event.stopPropagation()}>
            <Menu
              anchorEl={menuAnchor}
              open={menuOpen}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  setMenuAnchor(null);
                  onDelete();
                }}
                sx={{ gap: "8px", color: collectionColors.danger, fontFamily: font, fontSize: "14px" }}
              >
                <DeleteOutlineIcon sx={{ width: 18, height: 18 }} />
                Delete collection
              </MenuItem>
            </Menu>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", padding: "4px" }}>
            {expanded ? (
              <KeyboardArrowUpIcon sx={{ width: 20, height: 20, color: collectionColors.textSecondary }} />
            ) : (
              <KeyboardArrowDownIcon sx={{ width: 20, height: 20, color: collectionColors.textSecondary }} />
            )}
          </Box>
        </Box>
      </Box>

      {/* Variants */}
      <Collapse in={expanded}>
        {detailError ? (
          // Shown in place of the contents rather than as a toast over a closed card:
          // the card stays open, says what went wrong, and offers the way out.
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "10px",
              padding: "20px",
              borderTop: `1px solid ${collectionColors.border}`,
            }}
          >
            <Typography sx={{ ...subtitleSx, color: collectionColors.danger }}>
              Could not load this collection: {detailError}
            </Typography>
            <Button onClick={onRetryDetail} sx={outlinedButtonSx}>
              Try again
            </Button>
          </Box>
        ) : isLoadingDetail ? (
          // The listing carries only what the closed card shows, so everything below
          // arrives on the first open. A spinner beats an accordion that opens onto a
          // collection that looks empty.
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: "28px",
              borderTop: `1px solid ${collectionColors.border}`,
            }}
          >
            <CircularProgress size={22} />
          </Box>
        ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            padding: "17px",
            borderTop: `1px solid ${collectionColors.border}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <FolderOutlinedIcon sx={{ width: 16, height: 16, color: collectionColors.textSecondary }} />
              <Typography
                sx={{
                  fontFamily: font,
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "20px",
                  color: collectionColors.textPrimary,
                }}
              >
                Variants
              </Typography>
            </Box>
            <Button
              startIcon={<AddIcon sx={{ width: 16, height: 16 }} />}
              onClick={onAddVariant}
              sx={accentButtonSx}
            >
              Add Variant
            </Button>
          </Box>

          {/* Two labelled rows rather than one right-aligned cluster: FILES is what the
              collection is built from, REPORTS is what comes out of it, and reports are a
              list that grows — they need the room. */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {/* FILES — the configuration file that shapes the client's view, and the
                About write-up it reads first. Neither is versioned: a new upload replaces
                the current one. The About file can also be removed outright, which is not
                the same thing — it takes the client's About tab away with it. */}
            <Box sx={fileRowSx}>
              <Typography sx={{ ...captionLabelSx, minWidth: "64px" }}>Files:</Typography>

              <Tooltip
                title={
                  collection.configFileName
                    ? "Controls how surveys and questions are presented to the client"
                    : "No configuration file applied to this collection yet"
                }
              >
                {collection.configFileName ? (
                  <Box sx={appliedChipSx}>
                    <CheckIcon sx={{ width: 12, height: 12 }} />
                    <Box component="span" sx={ellipsisSx}>
                      {collection.configFileName}
                    </Box>
                  </Box>
                ) : (
                  <Typography sx={emptyFileSx}>No configuration file</Typography>
                )}
              </Tooltip>

              {/* The applied file name does not survive a reload (the Survey API has nowhere
                  to store it), so downloading the configuration is how you see what is
                  actually applied. Read-only, so it works without the admin key. */}
              <Tooltip title="Download the applied configuration">
                <Box
                  component="button"
                  type="button"
                  aria-label="Download configuration file"
                  onClick={onDownloadConfig}
                  sx={iconButtonSx}
                >
                  <FileDownloadOutlinedIcon
                    sx={{ width: 16, height: 16, color: collectionColors.textSecondary }}
                  />
                </Box>
              </Tooltip>
              <Button
                startIcon={<AddIcon sx={{ width: 16, height: 16 }} />}
                onClick={onUploadConfigFile}
                sx={outlinedButtonSx}
              >
                Configuration File
              </Button>

              {collection.aboutFileName ? (
                /* Remove lives inside the chip, after the name, and only surfaces on
                   hover: the file is the subject, the action is a detail of it. It keeps
                   its space in the layout while hidden, so the row does not jump when the
                   pointer arrives. `focus-within` is what makes it reachable by keyboard,
                   where there is no hover to trigger. */
                <Box
                  sx={{
                    ...appliedChipSx,
                    "&:hover .about-remove, &:focus-within .about-remove": {
                      opacity: 1,
                      pointerEvents: "auto",
                    },
                  }}
                >
                  <CheckIcon sx={{ width: 12, height: 12 }} />
                  <Tooltip title="The study overview shown to the client">
                    <Box component="span" sx={ellipsisSx}>
                      {collection.aboutFileName}
                    </Box>
                  </Tooltip>
                  <Tooltip title="Remove the About the Study file">
                    <Box
                      component="button"
                      type="button"
                      className="about-remove"
                      aria-label="Remove About the Study file"
                      onClick={onRemoveAboutFile}
                      sx={{
                        ...iconButtonSx,
                        width: "16px",
                        height: "16px",
                        marginLeft: "2px",
                        borderRadius: "4px",
                        opacity: 0,
                        pointerEvents: "none",
                        transition: "opacity 120ms ease",
                        "&:hover": { background: collectionColors.dangerSurface },
                      }}
                    >
                      <DeleteOutlineIcon
                        sx={{ width: 12, height: 12, color: collectionColors.textSecondary }}
                      />
                    </Box>
                  </Tooltip>
                </Box>
              ) : (
                <Tooltip title="No About the Study file uploaded yet">
                  <Typography sx={emptyFileSx}>No about file</Typography>
                </Tooltip>
              )}
              <Button
                startIcon={<AddIcon sx={{ width: 16, height: 16 }} />}
                onClick={onUploadAboutFile}
                sx={outlinedButtonSx}
              >
                About the Study
              </Button>
            </Box>

            {/* REPORTS — the deliverables the client downloads. Unlike the files above,
                this is a list that grows, and removing one is the only way to take it
                back: replacing a report is not a concept, and the stored file is exactly
                what the client gets. */}
            <Box sx={fileRowSx}>
              <Typography sx={{ ...captionLabelSx, minWidth: "64px" }}>Reports:</Typography>

              {collection.reports.length === 0 ? (
                <Tooltip title="No reports published to this collection yet">
                  <Typography sx={emptyFileSx}>No reports yet</Typography>
                </Tooltip>
              ) : (
                /* Remove lives inside the chip, after the name, and only on hover — the
                   same shape the About file uses above. */
                collection.reports.map((report) => (
                  <Box
                    key={report.id}
                    sx={{
                      ...appliedChipSx,
                      "&:hover .report-remove, &:focus-within .report-remove": {
                        opacity: 1,
                        pointerEvents: "auto",
                      },
                    }}
                  >
                    <CheckIcon sx={{ width: 12, height: 12 }} />
                    <Tooltip title={`${report.fileName} — ${formatFileSize(report.sizeBytes)}`}>
                      <Box component="span" sx={ellipsisSx}>
                        {report.title}
                      </Box>
                    </Tooltip>
                    <Tooltip title="Remove this report">
                      <Box
                        component="button"
                        type="button"
                        className="report-remove"
                        aria-label={`Remove report ${report.title}`}
                        onClick={() => onRemoveReport(report)}
                        sx={{
                          ...iconButtonSx,
                          width: "16px",
                          height: "16px",
                          marginLeft: "2px",
                          borderRadius: "4px",
                          opacity: 0,
                          pointerEvents: "none",
                          transition: "opacity 120ms ease",
                          "&:hover": { background: collectionColors.dangerSurface },
                        }}
                      >
                        <DeleteOutlineIcon
                          sx={{ width: 12, height: 12, color: collectionColors.textSecondary }}
                        />
                      </Box>
                    </Tooltip>
                  </Box>
                ))
              )}

              <Button
                startIcon={<AddIcon sx={{ width: 16, height: 16 }} />}
                onClick={onUploadReports}
                sx={outlinedButtonSx}
              >
                Reports
              </Button>
            </Box>
          </Box>

          {collection.variants.length === 0 ? (
            <Typography sx={subtitleSx}>
              No variants in this collection yet. Add one to start collecting responses.
            </Typography>
          ) : (
            collection.variants.map((variant) => (
              <VariantRow
                key={variant.surveyOriginalId}
                variant={variant}
                historyOpen={openHistoryFor === variant.surveyOriginalId}
                onToggleHistory={() => onToggleVariantHistory(variant.surveyOriginalId)}
                onRename={(label) => onRenameVariant(variant, label)}
                onUploadVersion={() => onUploadVersion(variant)}
                onSetPrimary={() => onSetPrimaryVariant(variant)}
                onRemove={() => onRemoveVariant(variant)}
              />
            ))
          )}

          {/* EARLY ACCESS — the only control here that changes what the client sees
              rather than what the collection is made of, so it sits below a rule at the
              foot of the card, away from the variant and file actions.

              Off is the standing rule: Results and Chat with Data stay hidden until the
              survey closes. On lifts that for this collection alone, which is why the
              label goes teal — an enabled card should be recognisable without reading it. */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              paddingTop: "12px",
              borderTop: `1px solid ${collectionColors.border}`,
            }}
          >
            <Tooltip
              title={
                earlyAccessEnabled
                  ? "Client users can open Results and Derin AI while this survey is still collecting"
                  : "Results and Derin AI stay hidden from client users until this survey closes"
              }
            >
              {/* Wrapped: a disabled Switch fires no events, so the Tooltip needs an
                  element of its own to listen on while a toggle is in flight. */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Switch
                  checked={earlyAccessEnabled}
                  disabled={isTogglingEarlyAccess}
                  onChange={onToggleEarlyAccess}
                  inputProps={{ "aria-label": "Enable early access" }}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: collectionColors.white,
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      background: collectionColors.buttonAccent,
                      opacity: 1,
                    },
                  }}
                />
              </Box>
            </Tooltip>

            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                sx={{
                  fontFamily: font,
                  fontWeight: 600,
                  fontSize: "14px",
                  lineHeight: "20px",
                  letterSpacing: "-0.150391px",
                  color: earlyAccessEnabled
                    ? collectionColors.buttonAccentHover
                    : collectionColors.textPrimary,
                }}
              >
                Enable Early Access
              </Typography>
              <Typography
                sx={{
                  ...subtitleSx,
                  color: earlyAccessEnabled
                    ? collectionColors.buttonAccentHover
                    : collectionColors.textSecondary,
                }}
              >
                Results and Derin AI (Chat with data)
              </Typography>
            </Box>

            {isTogglingEarlyAccess && (
              <CircularProgress size={16} sx={{ color: collectionColors.textSecondary }} />
            )}
          </Box>
        </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default CollectionCard;
